import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMobileAlt, FaWallet, FaRupeeSign, FaSatelliteDish, FaBolt, FaWater, FaGasPump, FaHome, FaCreditCard } from 'react-icons/fa';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

const operatorMap = {
  Airtel: 10,
  Vodafone: 11,
  Jio: 12,
  BSNL: 13,
  Idea: 14,
  'Vi': 11,
};

const dthOperators = ['Tata Sky', 'Airtel Digital TV', 'Dish TV', 'Videocon D2H', 'Sun Direct', 'Reliance Digital TV'];

const circleOptions = [
  'Delhi NCR',
  'Mumbai',
  'Maharashtra Goa',
  'Andhra Pradesh Telangana',
  'Assam',
  'Bihar Jharkhand',
  'Chennai',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu Kashmir',
  'Karnataka',
  'Kerala',
  'Kolkata',
  'Madhya Pradesh Chhattisgarh',
  'North East',
  'Orissa',
  'Punjab',
  'Rajasthan',
  'Tamil Nadu',
  'UP East',
  'UP West',
  'West Bengal',
];

// Utility bill services
const utilityServices = [
  { id: 'electricity', name: 'Electricity', icon: FaBolt, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'gas', name: 'Gas', icon: FaGasPump, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'water', name: 'Water', icon: FaWater, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'landline', name: 'Landline', icon: FaHome, fieldLabel: 'Landline Number', placeholder: 'Enter landline number' },
  { id: 'broadband', name: 'Broadband', icon: FaCreditCard, fieldLabel: 'Account Number', placeholder: 'Enter account number' },
  { id: 'insurance', name: 'Insurance', icon: FaCreditCard, fieldLabel: 'Policy Number', placeholder: 'Enter policy number' },
];

const RechargeBillPage = () => {
  const navigate = useNavigate();
  const [mainCategory, setMainCategory] = useState('recharge'); // 'recharge' or 'utilities'
  const [activeTab, setActiveTab] = useState('prepaid'); // For recharge: 'prepaid', 'postpaid', 'dth'
  const [selectedUtility, setSelectedUtility] = useState(utilityServices[0].id); // For utilities
  const [formData, setFormData] = useState({
    mobile: '',
    operator: '',
    circle: circleOptions[0],
    amount: '',
    operator_id: '', // For utilities
    ad1: '',
    ad2: '',
    ad3: '',
  });
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingBill, setFetchingBill] = useState(false);
  const [billFetchData, setBillFetchData] = useState(null);
  const [billInfo, setBillInfo] = useState(null);
  const [dthInfo, setDthInfo] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [operators, setOperators] = useState([]); // All operators from PaySprint
  const [filteredOperators, setFilteredOperators] = useState([]); // Filtered by selected utility
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null); // Selected operator object with viewbill, regex, etc.

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const filterOperatorsByCategory = useCallback((ops) => {
    if (!ops || ops.length === 0) {
      setFilteredOperators([]);
      return;
    }

    // Map utility service IDs to PaySprint categories
    const categoryMap = {
      'electricity': ['electricity', 'power', 'energy'],
      'gas': ['gas', 'lpg', 'cng'],
      'water': ['water', 'jal'],
      'landline': ['landline', 'telephone'],
      'broadband': ['broadband', 'internet', 'fiber'],
      'insurance': ['insurance', 'lic', 'life insurance', 'health insurance']
    };

    const categories = categoryMap[selectedUtility] || [];
    
    // Filter operators by category (case-insensitive)
    const filtered = ops.filter(op => {
      const opCategory = (op.category || '').toLowerCase();
      const opName = (op.name || '').toLowerCase();
      const opDisplayName = (op.displayname || '').toLowerCase();
      
      return categories.some(cat => 
        opCategory.includes(cat.toLowerCase()) || 
        opName.includes(cat.toLowerCase()) ||
        opDisplayName.includes(cat.toLowerCase())
      );
    });

    setFilteredOperators(filtered);
    
    // Clear operator_id if current selection is not in filtered list
    if (formData.operator_id && !filtered.find(op => String(op.id) === String(formData.operator_id))) {
      setFormData(prev => ({ ...prev, operator_id: '', ad1: '', ad2: '', ad3: '' }));
      setSelectedOperator(null);
    }
  }, [selectedUtility, formData.operator_id]);

  const fetchOperators = useCallback(async () => {
    setLoadingOperators(true);
    setError('');
    try {
      const response = await api.get('/api/bill-payment/operators', {
        params: { mode: 'online' }
      });

      if (response.data.success && response.data.data?.data) {
        const ops = response.data.data.data;
        setOperators(ops);
        // Filter will be applied by the useEffect hook
      } else {
        setError('Failed to load operators. Please try again.');
        setOperators([]);
        setFilteredOperators([]);
      }
    } catch (err) {
      console.error('Failed to fetch operators', err);
      setError('Failed to load operators. Please try again.');
      setOperators([]);
      setFilteredOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  }, []);

  // Fetch operators when utilities tab is selected
  useEffect(() => {
    if (mainCategory === 'utilities') {
      fetchOperators();
    }
  }, [mainCategory, fetchOperators]);

  // Filter operators when utility service changes
  useEffect(() => {
    if (mainCategory === 'utilities' && operators.length > 0) {
      filterOperatorsByCategory(operators);
    }
  }, [selectedUtility, operators, mainCategory, filterOperatorsByCategory]);

  const fetchWalletBalance = async () => {
    try {
      const response = await api.get('/api/wallet/balance');
      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance', err);
    }
  };

  const handleMainCategoryChange = (category) => {
    setMainCategory(category);
    if (category === 'recharge') {
      setActiveTab('prepaid');
    } else {
      setSelectedUtility(utilityServices[0].id);
    }
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setDthInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setDthInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleUtilityChange = (utilityId) => {
    setSelectedUtility(utilityId);
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate canumber against operator regex
  const validateCanumber = (canumber, operator) => {
    if (!operator || !operator.regex) return true; // No regex = valid
    
    try {
      const regex = new RegExp(operator.regex);
      return regex.test(canumber);
    } catch (e) {
      console.error('Invalid regex pattern:', operator.regex);
      return true; // If regex is invalid, allow it
    }
  };

  // Validate ad fields against operator regex
  const validateAdField = (value, regex) => {
    if (!regex) return true;
    try {
      const regexPattern = new RegExp(regex);
      return regexPattern.test(value);
    } catch (e) {
      return true;
    }
  };

  const handleFetchBill = async () => {
    if (mainCategory === 'utilities') {
      if (!formData.mobile || formData.mobile.trim() === '') {
        setError('Enter consumer number before fetching the bill.');
        return;
      }
      if (!formData.operator_id || !selectedOperator) {
        setError('Select provider/operator before fetching the bill.');
        return;
      }

      // Validate canumber against operator regex
      if (!validateCanumber(formData.mobile, selectedOperator)) {
        setError(`Invalid ${selectedOperator.displayname || 'consumer number'} format. Please check and try again.`);
        return;
      }

      // Validate ad fields if present
      if (selectedOperator.ad1_name && formData.ad1 && !validateAdField(formData.ad1, selectedOperator.ad1_regex)) {
        setError(`Invalid ${selectedOperator.ad1_name} format.`);
        return;
      }
      if (selectedOperator.ad2_name && formData.ad2 && !validateAdField(formData.ad2, selectedOperator.ad2_regex)) {
        setError(`Invalid ${selectedOperator.ad2_name} format.`);
        return;
      }
      if (selectedOperator.ad3_name && formData.ad3 && !validateAdField(formData.ad3, selectedOperator.ad3_regex)) {
        setError(`Invalid ${selectedOperator.ad3_name} format.`);
        return;
      }
    } else {
      if (!formData.mobile || formData.mobile.length !== 10) {
        setError('Enter a valid 10 digit mobile number before fetching the bill.');
        return;
      }
      if (!formData.operator) {
        setError('Select operator before fetching the bill.');
        return;
      }
      if (!getBillOperatorId()) {
        setError('Selected operator is not supported yet.');
        return;
      }
    }

    setFetchingBill(true);
    setError('');
    try {
      const payload = {
        operator: Number(mainCategory === 'utilities' ? formData.operator_id : getBillOperatorId()),
        canumber: formData.mobile,
        mode: 'online',
      };

      // Add ad1/ad2/ad3 only if they exist and have values
      if (formData.ad1) payload.ad1 = formData.ad1;
      if (formData.ad2) payload.ad2 = formData.ad2;
      if (formData.ad3) payload.ad3 = formData.ad3;

      const response = await api.post('/api/bill-payment/fetch-bill', payload);

      if (response.data.success) {
        const data = response.data.data?.data || response.data.data;
        setBillFetchData(data);
        setBillInfo(data);
        if (data.amount || data.billAmount) {
          setFormData((prev) => ({ ...prev, amount: data.amount || data.billAmount || '' }));
        }
        toast.success('Bill details fetched');
      } else {
        // ✅ FIX: Show user-friendly error message
        const errorMessage = response.data.message || 'Unable to fetch bill details';
        setError(errorMessage);
        
        // Show toast for service down errors
        if (response.data.error_code === 'SERVICE_DOWN' || response.data.response_code === 4) {
          toast.error('Bill payment service is temporarily unavailable. Please try again later.');
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (err) {
      console.error('Fetch bill error', err);
      
      // ✅ FIX: Handle different HTTP status codes
      const status = err.response?.status;
      const errorData = err.response?.data;
      
      let errorMessage = 'Unable to fetch bill details';
      
      if (status === 503 || errorData?.error_code === 'SERVICE_DOWN' || errorData?.response_code === 4) {
        errorMessage = 'Bill payment service is temporarily down. Please try again later.';
        toast.error(errorMessage);
      } else if (errorData?.message) {
        errorMessage = errorData.message;
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      
      setError(errorMessage);
    } finally {
      setFetchingBill(false);
    }
  };

  const getBillOperatorId = () => operatorMap[formData.operator] || formData.operator;
  const getRechargeOperatorValue = () => formData.operator;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation based on category
    if (mainCategory === 'utilities') {
      // Utilities validation
      if (!formData.mobile || formData.mobile.trim() === '') {
        const utility = utilityServices.find(u => u.id === selectedUtility);
        setError(`Please enter ${utility?.fieldLabel || 'consumer number'}.`);
        return;
      }
      if (!formData.operator_id) {
        setError('Please select an operator/provider.');
        return;
      }
    } else {
      // Recharge validation
      if (activeTab === 'dth') {
        if (!formData.mobile || formData.mobile.length < 6) {
          setError('Please enter a valid customer/subscriber ID.');
          return;
        }
      } else {
        if (!formData.mobile || formData.mobile.length !== 10) {
          setError('Please enter a valid 10 digit mobile number.');
          return;
        }
      }
      if (!formData.operator) {
        setError('Please select an operator.');
        return;
      }
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (balance !== null && Number(formData.amount) > Number(balance)) {
      setError('Insufficient wallet balance.');
      return;
    }
    if (activeTab === 'postpaid' && !billFetchData) {
      setError('Please fetch bill details before paying.');
      return;
    }

    setLoading(true);
    try {
      let endpoint;
      let payload;

      if (mainCategory === 'utilities') {
        // Utilities bill payment
        const op = selectedOperator; // from operator list
        
        // Check viewbill: if 0, direct pay; if 1, must have fetched bill
        if (String(op?.viewbill) === "1" && !billFetchData) {
          setError('Please fetch bill details before paying.');
          setLoading(false);
          return;
        }

        endpoint = '/api/bill-payment/pay-bill';
        payload = {
          operator: Number(formData.operator_id),
          canumber: formData.mobile,
          amount: formData.amount,
          mode: 'online',
        };

        // Add ad1/ad2/ad3 only if they exist and have values
        if (formData.ad1) payload.ad1 = formData.ad1;
        if (formData.ad2) payload.ad2 = formData.ad2;
        if (formData.ad3) payload.ad3 = formData.ad3;
      } else if (activeTab === 'postpaid') {
        endpoint = '/api/bill-payment/pay-bill';
        payload = {
          operator_id: getBillOperatorId(),
          consumer_number: formData.mobile,
          amount: formData.amount,
          payment_type: 'postpaid',
          reference_id: uuidv4(),
          bill_fetch: billFetchData,
        };
      } else if (activeTab === 'dth') {
        endpoint = '/api/recharge/process';
        payload = {
          mobile: formData.mobile,
          operator: getRechargeOperatorValue(),
          amount: formData.amount,
          type: 'dth',
          idempotency_key: uuidv4(),
        };
      } else {
        endpoint = '/api/recharge/process';
        payload = {
          mobile: formData.mobile,
          operator: getRechargeOperatorValue(),
          amount: formData.amount,
          type: 'mobile',
          idempotency_key: uuidv4(),
        };
      }

      const response = await api.post(endpoint, payload);
      if (response.data.success) {
        let successText = 'Transaction successful!';
        if (mainCategory === 'utilities') {
          const utility = utilityServices.find(u => u.id === selectedUtility);
          successText = `${utility?.name || 'Bill'} payment successful!`;
        } else if (activeTab === 'postpaid') {
          successText = 'Bill payment successful!';
        } else if (activeTab === 'dth') {
          successText = 'DTH recharge successful!';
        } else {
          successText = 'Recharge successful!';
        }
        setSuccessMessage(successText);
        toast.success(successText);
        fetchWalletBalance();
        setBillFetchData(null);
        setBillInfo(null);
        setFormData((prev) => ({ ...prev, amount: '' }));
      } else {
        setError(response.data.message || 'Transaction failed. Please try again.');
      }
    } catch (err) {
      console.error('Transaction error', err);
      setError(err.response?.data?.message || 'Transaction failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-blue-700 text-white py-6 shadow">
        <div className="max-w-5xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm font-semibold text-blue-100 mb-4 hover:text-white transition"
            type="button"
          >
            <FaArrowLeft className="mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold">Recharge & Bill Payment</h1>
          <p className="text-blue-100 mt-2">
            Instant recharge of your prepaid/postpaid mobile or DTH connection with the BT India dashboard look & feel.
          </p>
        </div>
      </div>

      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {/* Wallet Balance */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wide text-gray-500">Wallet Balance</p>
              <div className="flex items-center text-2xl font-semibold text-blue-700">
                <FaWallet className="mr-2" />
                {balance !== null ? `₹${Number(balance).toFixed(2)}` : '---'}
              </div>
            </div>

            {/* Main Category Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition ${
                  mainCategory === 'recharge'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => handleMainCategoryChange('recharge')}
                type="button"
              >
                <FaMobileAlt className="inline mr-2" />
                Recharge
              </button>
              <button
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition ${
                  mainCategory === 'utilities'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => handleMainCategoryChange('utilities')}
                type="button"
              >
                <FaBolt className="inline mr-2" />
                Utilities & Bills
              </button>
            </div>

            {/* Sub-tabs for Recharge */}
            {mainCategory === 'recharge' && (
              <div className="flex border border-gray-200 rounded-xl overflow-hidden mb-6">
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'prepaid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('prepaid')}
                  type="button"
                >
                  PREPAID
                </button>
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'postpaid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('postpaid')}
                  type="button"
                >
                  POSTPAID
                </button>
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'dth' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('dth')}
                  type="button"
                >
                  DTH
                </button>
              </div>
            )}

            {/* Utility Service Selection */}
            {mainCategory === 'utilities' && (
              <div className="mb-6">
                <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Select Utility Service</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {utilityServices.map((utility) => {
                    const IconComponent = utility.icon;
                    return (
                      <button
                        key={utility.id}
                        type="button"
                        onClick={() => handleUtilityChange(utility.id)}
                        className={`p-4 border-2 rounded-lg text-center transition ${
                          selectedUtility === utility.id
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <IconComponent className="mx-auto mb-2 text-xl" />
                        <div className="text-sm font-semibold">{utility.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">
                    {mainCategory === 'utilities'
                      ? (selectedOperator?.displayname || utilityServices.find(u => u.id === selectedUtility)?.fieldLabel || 'Consumer Number')
                      : activeTab === 'dth'
                      ? 'Customer ID'
                      : 'Mobile No'}
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      mainCategory === 'utilities'
                        ? utilityServices.find(u => u.id === selectedUtility)?.placeholder || 'Enter consumer number'
                        : activeTab === 'dth'
                        ? 'Enter customer/subscriber ID'
                        : 'Enter 10 digit mobile number'
                    }
                    value={formData.mobile}
                    onChange={(e) =>
                      handleInputChange(
                        'mobile',
                        mainCategory === 'utilities' || activeTab === 'dth'
                          ? e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 20)
                          : e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
                      )
                    }
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">
                    {mainCategory === 'utilities' ? 'Provider/Operator' : 'Operator'}
                    {mainCategory === 'utilities' && loadingOperators && (
                      <span className="ml-2 text-xs text-gray-400 font-normal">(Loading...)</span>
                    )}
                  </label>
                  {mainCategory === 'utilities' ? (
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.operator_id}
                      onChange={(e) => {
                        const opId = e.target.value;
                        handleInputChange('operator_id', opId);
                        // Find and set selected operator
                        const op = filteredOperators.find(o => String(o.id) === String(opId));
                        setSelectedOperator(op || null);
                        // Clear ad fields when operator changes
                        setFormData(prev => ({ ...prev, ad1: '', ad2: '', ad3: '' }));
                      }}
                      disabled={loadingOperators}
                    >
                      <option value="">
                        {loadingOperators 
                          ? 'Loading operators...' 
                          : filteredOperators.length === 0 
                          ? 'No operators available' 
                          : 'Select provider/operator'}
                      </option>
                      {filteredOperators.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.displayname || operator.name || `Operator ${operator.id}`}
                          {operator.category ? ` (${operator.category})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.operator}
                      onChange={(e) => handleInputChange('operator', e.target.value)}
                    >
                      <option value="">Select operator</option>
                      {(activeTab === 'dth' ? dthOperators : Object.keys(operatorMap)).map(
                        (operator) => (
                          <option key={operator} value={operator}>
                            {operator}
                          </option>
                        )
                      )}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Amount</label>
                  <div className="flex items-center border border-gray-200 rounded-lg px-4 py-3 mt-1 focus-within:ring-2 focus-within:ring-blue-500">
                    <FaRupeeSign className="text-gray-400 mr-2" />
                    <input
                      type="number"
                      className="w-full focus:outline-none"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      min="10"
                    />
                  </div>
                </div>
                {mainCategory === 'recharge' && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Circle</label>
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.circle}
                      onChange={(e) => handleInputChange('circle', e.target.value)}
                    >
                      {circleOptions.map((circle) => (
                        <option key={circle} value={circle}>
                          {circle}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {mainCategory === 'recharge' && activeTab === 'postpaid' && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      Fetch the latest bill before paying to auto-fill the exact amount.
                    </p>
                    {billInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Bill fetched for account {billInfo?.customerName || billInfo?.name || formData.mobile}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleFetchBill}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    {fetchingBill ? 'Fetching...' : 'Fetch Bill Details'}
                  </button>
                </div>
              )}

              {/* Dynamic ad1/ad2/ad3 fields based on selected operator */}
              {mainCategory === 'utilities' && selectedOperator && (
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  {selectedOperator.ad1_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad1_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad1_name}`}
                        value={formData.ad1}
                        onChange={(e) => handleInputChange('ad1', e.target.value)}
                      />
                    </div>
                  )}
                  {selectedOperator.ad2_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad2_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad2_name}`}
                        value={formData.ad2}
                        onChange={(e) => handleInputChange('ad2', e.target.value)}
                      />
                    </div>
                  )}
                  {selectedOperator.ad3_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad3_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad3_name}`}
                        value={formData.ad3}
                        onChange={(e) => handleInputChange('ad3', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Show fetch bill button only if viewbill = 1 */}
              {mainCategory === 'utilities' && selectedOperator && String(selectedOperator.viewbill) === "1" && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      Fetch the latest bill before paying to auto-fill the exact amount.
                    </p>
                    {billInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Bill fetched for account {billInfo?.customerName || billInfo?.name || formData.mobile}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleFetchBill}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    {fetchingBill ? 'Fetching...' : 'Fetch Bill Details'}
                  </button>
                </div>
              )}

              {mainCategory === 'recharge' && activeTab === 'dth' && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      View subscriber info to auto-fill the recommended recharge amount.
                    </p>
                    {dthInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Subscriber: {dthInfo?.CustomerName || dthInfo?.name || formData.mobile} | Balance:{' '}
                        {dthInfo?.Balance || dthInfo?.MonthlyRecharge || 'N/A'}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.mobile || !formData.operator) {
                        setError('Enter customer ID and select operator to fetch DTH info.');
                        return;
                      }
                      setLoading(true);
                      setError('');
                      try {
                        const response = await api.post('/api/hlr/dth-info', {
                          canumber: formData.mobile,
                          op: getRechargeOperatorValue(),
                        });
                        if (response.data.success) {
                          const data = response.data.data?.data || response.data.data;
                          setDthInfo(data);
                          if (data?.MonthlyRecharge || data?.Balance) {
                            setFormData((prev) => ({
                              ...prev,
                              amount: data.MonthlyRecharge || data.Balance || '',
                            }));
                          }
                          toast.success('DTH info fetched successfully');
                        } else {
                          setError(response.data.message || 'Unable to fetch DTH info');
                        }
                      } catch (err) {
                        console.error('DTH info error', err);
                        setError(err.response?.data?.message || 'Unable to fetch DTH info');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    <FaSatelliteDish className="inline mr-2" />
                    View DTH Info
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-4 bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mt-4 bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center transition"
              >
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    {mainCategory === 'utilities' ? (
                      <>
                        <FaBolt className="mr-2" />
                        Pay {utilityServices.find(u => u.id === selectedUtility)?.name || 'Bill'}
                      </>
                    ) : activeTab === 'dth' ? (
                      <>
                        <FaSatelliteDish className="mr-2" />
                        Continue To Recharge
                      </>
                    ) : (
                      <>
                        <FaMobileAlt className="mr-2" />
                        Continue To Recharge
                      </>
                    )}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RechargeBillPage;


